import { ref } from 'vue'
import axios from 'axios'

// Default configuration (Mission Pest Control)
const defaultConfig = {
  companySlug: 'mission',
  companyName: 'Mission Pest Control',
  theme: {
    colors: {
      primary: '#ce2029',
      secondary: '#13322B',
      accent: '#F3C623',
      background: '#F5F5F5',
      surface: '#FFFFFF',
      text: '#13322B',
    },
    fonts: {
      heading: 'Fugaz One',
      body: 'Lexend Exa',
      subheading: 'Poltawski Nowy',
    },
    logo: 'assets/mission/mission-logo.svg',
  },
  integration: {
    officeIDs: '714',
    sourceID: 10207,
    zipValidation: {
      enabled: true,
      pattern: /^9[0-2]\d{3}$/,
      message: 'We only service San Diego County (ZIP codes 90000-92999)',
    },
  },
  formFields: {
    pests: {
      ants: true,
      fleas: true,
      roaches: true,
      wasps: true,
      spiders: true,
      rodents: true,
    },
  },
  customText: {
    welcomeMessage: 'Sign up for pest control in 4 easy steps',
    companyTagline: 'Professional Pest Control Services',
  },
}

// Reactive configuration state
export const companyConfig = ref({ ...defaultConfig })
export const configLoading = ref(false)
export const configError = ref(null)
export const clerkRawResponse = ref(null) // Store raw response from Clerk

// API base URL
const getApiBaseUrl = () => {
  return process.env.NODE_ENV === 'development'
    ? 'http://localhost:3000/api'
    : 'https://api.getpestcontrol.services/api'
}

/**
 * Load company configuration from Clerk organization metadata
 * @param {string} companySlug - The company slug from URL
 * @returns {Promise<Object>} - The loaded configuration
 */
export async function loadCompanyConfig(companySlug) {
  // If no slug provided, use default config
  if (!companySlug || companySlug === 'mission') {
    companyConfig.value = { ...defaultConfig }
    clerkRawResponse.value = { source: 'default', data: null }
    return companyConfig.value
  }

  configLoading.value = true
  configError.value = null
  clerkRawResponse.value = null

  try {
    const response = await axios.get(`${getApiBaseUrl()}/company/${companySlug}/config`)

    // Store the raw response from Clerk for debugging
    clerkRawResponse.value = {
      source: 'clerk',
      url: `${getApiBaseUrl()}/company/${companySlug}/config`,
      status: response.status,
      data: response.data
    }

    // Merge fetched config with defaults (in case some fields are missing)
    const fetchedConfig = response.data

    companyConfig.value = {
      companySlug: fetchedConfig.companySlug || companySlug,
      companyName: fetchedConfig.companyName || companySlug,
      organizationLogoUrl: fetchedConfig.organizationLogoUrl, // Clerk organization logo URL
      theme: {
        ...defaultConfig.theme,
        ...fetchedConfig.theme,
        colors: {
          ...defaultConfig.theme.colors,
          ...fetchedConfig.theme?.colors,
        },
        fonts: {
          ...defaultConfig.theme.fonts,
          ...fetchedConfig.theme?.fonts,
        },
      },
      integration: {
        ...defaultConfig.integration,
        ...fetchedConfig.integration,
      },
      formFields: {
        ...defaultConfig.formFields,
        ...fetchedConfig.formFields,
      },
      customText: {
        ...defaultConfig.customText,
        ...fetchedConfig.customText,
      },
    }

    return companyConfig.value
  } catch (error) {
    console.error('Error loading company config:', error)
    configError.value = error.response?.data?.error || error.message

    // Store error response for debugging
    clerkRawResponse.value = {
      source: 'clerk',
      url: `${getApiBaseUrl()}/company/${companySlug}/config`,
      status: error.response?.status || 'error',
      error: error.response?.data || error.message
    }

    // Fall back to default config on error
    companyConfig.value = { ...defaultConfig }
    return companyConfig.value
  } finally {
    configLoading.value = false
  }
}

/**
 * Get the current company slug
 * @returns {string} - The current company slug
 */
export function getCurrentCompanySlug() {
  return companyConfig.value.companySlug
}

/**
 * Reset to default configuration
 */
export function resetToDefault() {
  companyConfig.value = { ...defaultConfig }
  configError.value = null
}
